<template>
    <UModal v-model:open="isOpen" title="Rediger oppgave">
        <template #body>
            <UForm
                :schema
                :state
                :validate-on="['input', 'blur']"
                @submit="onSave"
                @error="onValidationError"
            >
                <div class="flex flex-col gap-2">
                    <UFormField
                        name="title"
                        label="Oppgavetittel"
                    />
                    <UInput
                        v-model="state.title"
                        class="w-full"
                        :placeholder="assignment?.title"
                    />
                    <UFormField
                        name="Kategori"
                        label="Kategori"
                    />
                    <USelect
                        v-model="state.type"
                        class="w-full"
                        :items="categoryOptions"
                        label-key="label"
                        value-key="value"
                    />
                    <UFormField
                        name="status"
                        label="Status"
                    />
                    <UInput
                        :model-value="state.status"
                        class="w-full"
                        disabled
                    />
                    <UFormField
                        name="attachments"
                        label="Vedlegg"
                    />
                    <assignment-file-uploader
                        :key="assignment?.uuid"
                        :show-action-buttons="false"
                        :doc-meta-uuids="documentMetaUuids"
                        @files-changed="handleFileChange"
                        @removed-uuid="handleRemovedUuid"
                    />
                </div>
                <div class="flex w-full justify-end gap-2 mt-4">
                    <UButton
                        class="w-32 justify-center"
                        label="Avbryt"
                        variant="outline"
                        color="neutral"
                        size="md"
                        @click="$emit('close')"
                    />
                    <UButton
                        type="submit"
                        class="w-32 justify-center"
                        label="Lagre oppgave"
                        size="md"
                    />
                </div>
            </UForm>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import {computed, ComputedRef, PropType, reactive, ref} from 'vue'
import {ActionableType, Assignment, EditAssignmentSchema} from '@/assignments/Assignment.type'
import AssignmentFileUploader from '@/assignments/AssignmentFileUploader.vue'
import {useAssignmentOptions} from '@/assignments/Assignment.options'
import type {ZodObject} from 'zod'
import {FormErrorEvent, FormSubmitEvent} from '@nuxt/ui'
import {updateAssignment} from '@/assignments/assignment.service'
import {fileUploadBatch} from '@/services/document.service'
import {getSelectedOrgId} from '@/tenants/org.service'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {ResourceLink} from '@/resource-link/resourceLink'

const isOpen = ref(false)

const emit = defineEmits(['close'])
const files = ref()
const removedDocUuids = ref<string[]>([])

const props = defineProps({
    assignment: {
        type: Object as () => Assignment,
    },
    onSaved: {
        type: Function as PropType<(assignment: Assignment) => void>,
        required: false,
    }
})

const schema = computed(() => EditAssignmentSchema) as ComputedRef<ZodObject<any>>
const documentMetaUuids = computed(() =>
    props.assignment?.resourceLinks?.filter(rl => rl.type === 'document').map(rl => rl.identifier) ?? []
)

const state = reactive({
    ...props.assignment,
    uuid: props.assignment?.uuid,
    dependencies: props.assignment?.dependencies.map(dep => dep.uuid)
})

const {categoryOptions} = useAssignmentOptions()

const handleOpen = () => {
    files.value = undefined
    removedDocUuids.value = []
    isOpen.value = true
}

const handleFileChange = (newFiles: File[]) => {
    files.value = newFiles
}

const handleRemovedUuid = (uuid: string) => {
    removedDocUuids.value.push(uuid)
}

const onSave = async (event: FormSubmitEvent<any>) => {
    const updatedAssignment = {
        ...props.assignment,
        ...event.data
    }

    if (updatedAssignment.context?.type === ActionableType.Bookkeepable) {
        updatedAssignment.context.documentMetaUuids = updatedAssignment.context.documentMetaUuids
            .filter((uuid: string) => !removedDocUuids.value.includes(uuid))
    }

    if (removedDocUuids.value.length) {
        updatedAssignment.resourceLinks = (updatedAssignment.resourceLinks ?? [])
            .filter((rl: ResourceLink) => !(rl.type === 'document' && removedDocUuids.value.includes(rl.identifier)))
    }

    if (files.value?.length) {
        const newDocs = await fileUploadBatch(files.value)

        // TODO: Refactor this to be more generic
        if(updatedAssignment.context?.type === ActionableType.Bookkeepable){
            updatedAssignment.context.documentMetaUuids = [...updatedAssignment.context.documentMetaUuids, ...newDocs.map(doc => doc.uuid)]
        }

        const newLinks: ResourceLink[] = newDocs.map((doc: DocumentMeta) => ({
            system: 'pracsys',
            tenantId: getSelectedOrgId()!,
            type: 'document',
            identifier: doc.uuid,
            display: doc.filename,
        }))
        updatedAssignment.resourceLinks = [...(updatedAssignment.resourceLinks ?? []), ...newLinks]
    }
    const saved = await updateAssignment(updatedAssignment)
    props.onSaved?.(saved)
    emit('close')
}

const onValidationError = (event: FormErrorEvent) => {
    // This will log why the submission is being blocked
    console.log('Validation failed:', event.errors)
}


defineExpose({handleOpen})

</script>

<style scoped>

</style>